import React from 'react';
import { FormattedMessage } from 'react-intl';

import { getUpsellModalContent } from '../../../../../components/upsell-modal/helper';
import FormattedCurrency from '../../../../../components/formatted-currency';
import { NonWalletProviderAccess } from '../../../../../constants/features';
import { LegacyAddOnsToPreserveThroughMigration } from '../migrate-plan-modal/constants';
import { messages } from './messages';

export const getLineItemPlanSummaryForDisplay = (
  visibleLineItems,
  addOnsToAdd,
  addOnsToRemove,
  isMigratingToNewestPlans,
  transitioningAddOnCodes = [],
) => {
  return visibleLineItems
    .filter((lineItem) => {
      if (lineItem.isAlphaUser) {
        return true;
      }

      if (lineItem.isIncludedInPlan) {
        return !isMigratingToNewestPlans;
      }

      // During migration, hide all paid add-ons except preserved ones (DropShip, FulfillmentProviderBundle).
      // addOnsToRemove only covers active, non-included add-ons — this catches the rest.
      if (isMigratingToNewestPlans && lineItem.addOnCode && !LegacyAddOnsToPreserveThroughMigration.includes(lineItem.addOnCode)) {
        return false;
      }

      return true;
    })
    .map((lineItem) => {
      const isDeactivated =
        !lineItem.isAlphaUser &&
        !lineItem.isIncludedInPlan &&
        (addOnsToRemove.includes(lineItem.addOnCode) || (!!lineItem.deactivationDateTimeUtc && !addOnsToAdd.includes(lineItem.addOnCode)));

      const isToggleable = !lineItem.isAlphaUser && !lineItem.isIncludedInPlan && !!lineItem.addOnCode && !isMigratingToNewestPlans;

      const isTransitioning = transitioningAddOnCodes.includes(lineItem.addOnCode);

      const getDisplayPrice = () => {
        if (lineItem.isIncludedInPlan || lineItem.isAlphaUser) {
          return <FormattedMessage {...messages.included} />;
        }
        if (isDeactivated) {
          return <FormattedCurrency currencyCode={lineItem.price.code} value={0} />;
        }
        if (lineItem.isTrial && lineItem.postTrialPrice?.value) {
          return lineItem.postTrialPrice?.value ? (
            <FormattedCurrency currencyCode={lineItem.postTrialPrice.code} value={lineItem.postTrialPrice.value} />
          ) : (
            <FormattedMessage {...messages.included} />
          );
        }
        return <FormattedCurrency currencyCode={lineItem.price.code} value={lineItem.price.value} />;
      };

      return {
        code: lineItem.lineItemCode,
        addOnCode: lineItem.addOnCode,
        nameMessageDescriptor: lineItem.nameMessageDescriptor,
        isTrial: lineItem.isTrial,
        price: lineItem.price,
        postTrialPrice: lineItem.postTrialPrice,
        displayPrice: getDisplayPrice(),
        trialEndDateTimeUtc: lineItem.trialEndDateTimeUtc,
        isDeactivated,
        isToggleable,
        isTransitioning,
      };
    });
};

export const LineItemPriorities = {
  AlphaUser: 0,
  PlanIncluded: 1,
  AddOnTrial: 2,
  AddOnPaid: 3,
};

// Priority order for what line item is displayed, given a feature
export const getLineItemPriority = (lineItem) => {
  if (lineItem.isAlphaUser) return LineItemPriorities.AlphaUser;
  if (lineItem.isIncludedInPlan) return LineItemPriorities.PlanIncluded;
  if (lineItem.addOnCode !== null && lineItem.isTrial) return LineItemPriorities.AddOnTrial;
  if (lineItem.addOnCode !== null && !lineItem.isTrial) return LineItemPriorities.AddOnPaid;
};

// Note: With 1:1 feature:addOn relationships, we only want to ever display 1 line item per feature.
export const filterLineItemsByFeatureUniqueness = (
  lineItems,
  addOnFeaturesReferences,
  planCurrencyCode,
  isSellerOnFeatureGatedPlanProfile,
  isMigratingToNewestPlans = false,
) => {
  // Use new lineup filtering when on feature-gated profile OR migrating to one
  const shouldUseNewLineupFiltering = isSellerOnFeatureGatedPlanProfile || isMigratingToNewestPlans;

  const addOnLineItemsMap = lineItems.reduce((encounteredLineItems, lineItem) => {
    const lineItemCode = lineItem.addOnCode || lineItem.featureIds[0];

    // If the line item doesn't have upsell content, we don't want to display it
    const upsellContent = getUpsellModalContent(lineItemCode);
    if (Object.keys(upsellContent).length === 0 || (upsellContent.isNewLineupBillingPlanUpsell && !shouldUseNewLineupFiltering)) {
      return encounteredLineItems;
    }

    if (shouldUseNewLineupFiltering) {
      const addOnLineItem = addOnFeaturesReferences.find((addOnFeature) => addOnFeature.addOnCode === lineItemCode);
      if (!addOnLineItem) {
        return encounteredLineItems;
      }
    }

    const lastEncounteredLineItem = encounteredLineItems[lineItemCode];
    // If we haven't encountered this line item yet, add it to the map
    if (!lastEncounteredLineItem) {
      return {
        ...encounteredLineItems,
        [lineItemCode]: lineItem,
      };
    }

    // If we have encountered this line item, we want to keep the one with the highest priority
    const lastEncounteredLineItemPriority = getLineItemPriority(lastEncounteredLineItem);
    const currentItemPriority = getLineItemPriority(lineItem);
    if (currentItemPriority < lastEncounteredLineItemPriority) {
      return {
        ...encounteredLineItems,
        [lineItemCode]: lineItem,
      };
    }

    return encounteredLineItems;
  }, {});

  const subscriptionLineItems = Object.values(addOnLineItemsMap);
  const lineItemsForDisplay = subscriptionLineItems.map((item) => {
    const lineItemCode = item.addOnCode || item.featureIds[0];
    const upsellContent = getUpsellModalContent(lineItemCode);
    const addOnPrice = addOnFeaturesReferences.find((addOnFeature) => addOnFeature.addOnCode === lineItemCode)?.price;
    const isIncluded = item.isIncludedInPlan;
    const hasItForFree = item.isIncludedInPlan || item.isTrial || item.isAlphaUser;
    const lineItemPrice = hasItForFree ? { code: planCurrencyCode, value: 0 } : addOnPrice;

    return {
      lineItemCode,
      addOnCode: item.addOnCode,
      isTrial: item.isTrial,
      isAlphaUser: item.isAlphaUser,
      isIncludedInPlan: isIncluded,
      price: lineItemPrice,
      postTrialPrice: item.isTrial && lineItemCode === NonWalletProviderAccess ? addOnPrice : null,
      trialEndDateTimeUtc: item.trialEndDateTimeUtc,
      nameMessageDescriptor: upsellContent.name,
      descriptionMessageDescriptor: upsellContent.description,
      deactivationDateTimeUtc: item.deactivationDateTimeUtc,
    };
  });

  return lineItemsForDisplay.sort((a, b) => a.nameMessageDescriptor.defaultMessage.localeCompare(b.nameMessageDescriptor.defaultMessage));
};

export const getAdditionalAnalyticsLicenseLimitForNewPlan = (targetPlan, subscriptionData, desiredNumberOfAdditionalAnalyticsLicenses) => {
  const newAnalyticsLicenseLimit = targetPlan?.analyticsLicenseLimit?.limit ?? 0;
  if (newAnalyticsLicenseLimit === 0) {
    return 0;
  }
  const currentTotal =
    (subscriptionData?.currentBillingPlan?.analyticsLicenseLimit?.limit ?? 0) -
    (subscriptionData?.currentBillingPlan?.additionalAnalyticsLicenses ?? 0) +
    desiredNumberOfAdditionalAnalyticsLicenses;

  const newAdditional = Math.max(0, currentTotal - newAnalyticsLicenseLimit);
  return newAdditional;
};
